import {
  EvaluationRequest,
  EvaluationResponse,
  EvaluationRunRow,
  EvaluationRunSummary,
  Persona,
  VehicleEvalPersona,
  VehicleEvalRequest,
  VehicleEvalRunStartResponse,
} from '@/pages/admin/types/EvaluationTypes'
import { apiClient } from '@/services/api'

export const judgeEvaluationApi = {
  /**
   * Get list of available personas for evaluation
   * Backend endpoint: GET /evaluations/personas
   */
  async getPersonas(): Promise<Persona[]> {
    try {
      const response = await apiClient.get<Persona[]>('/evaluations/personas')
      return response
    } catch (error) {
      console.error('Failed to fetch personas:', error)
      throw new Error('Failed to fetch personas')
    }
  },

  /**
   * Run evaluation for selected personas
   * Backend endpoint: POST /evaluations/run
   * This will wait for completion and return full results
   */
  async runEvaluation(request: EvaluationRequest): Promise<EvaluationResponse> {
    try {
      const response = await apiClient.post<EvaluationResponse>('/evaluations/run', request)
      return response
    } catch (error) {
      console.error('Failed to run evaluation:', error)
      throw new Error('Failed to run evaluation')
    }
  },

  async getVehicleEvalPersonas(): Promise<VehicleEvalPersona[]> {
    try {
      return await apiClient.get<VehicleEvalPersona[]>('/evaluations/vehicle-eval/personas')
    } catch (error) {
      console.error('Failed to fetch vehicle eval personas:', error)
      throw new Error('Failed to fetch vehicle eval personas')
    }
  },

  async runVehicleEval(request: VehicleEvalRequest): Promise<VehicleEvalRunStartResponse> {
    try {
      return await apiClient.post<VehicleEvalRunStartResponse>('/evaluations/vehicle-eval/run', request)
    } catch (error) {
      console.error('Failed to run vehicle eval:', error)
      throw new Error('Failed to start vehicle evaluation')
    }
  },

  async listRuns(
    dealershipId: string,
    evalType?: 'conversation' | 'vehicle-eval',
    limit = 30,
  ): Promise<EvaluationRunSummary[]> {
    try {
      const params = new URLSearchParams({
        dealershipId,
        limit: String(limit),
      })
      if (evalType) params.set('evalType', evalType)
      return await apiClient.get<EvaluationRunSummary[]>(`/evaluations/runs?${params.toString()}`)
    } catch (error) {
      console.error('Failed to list evaluation runs:', error)
      throw new Error('Failed to list evaluation runs')
    }
  },

  async getRunResults(runId: string): Promise<EvaluationRunRow[]> {
    try {
      return await apiClient.get<EvaluationRunRow[]>(`/evaluations/runs/${runId}`)
    } catch (error) {
      console.error('Failed to fetch run results:', error)
      throw new Error('Failed to fetch run results')
    }
  },

  async getLatestVehicleEvalResults(dealershipId: string): Promise<EvaluationRunRow[]> {
    try {
      return await apiClient.get<EvaluationRunRow[]>(
        `/evaluations/runs/latest?dealershipId=${encodeURIComponent(dealershipId)}&evalType=vehicle-eval`,
      )
    } catch (error) {
      console.error('Failed to fetch latest vehicle eval results:', error)
      throw new Error('Failed to fetch latest vehicle eval results')
    }
  },

  /**
   * Get evaluation results for a specific customer
   * Backend endpoint: GET /evaluations/customer/:customerId
   */
  async getEvaluationByCustomer(
    customerId: string,
    dealershipId: string,
  ): Promise<{
    id: string
    runId: string
    overallScore: number
    judgeJustification: string | null
    judgeComments: string | null
    subscores: Array<{
      criterion: string
      rating: number
      pointsDeducted: number
      maxPoints: number
      explanation?: string
    }>
    timestamp: string
    personaId?: string
    personaName?: string | null
    customerName?: string | null
  } | null> {
    try {
      const response = await apiClient.get<{
        id: string
        runId: string
        overallScore: number
        judgeJustification: string | null
        judgeComments: string | null
        subscores: Array<{
          criterion: string
          rating: number
          pointsDeducted: number
          maxPoints: number
          explanation?: string
        }>
        timestamp: string
        personaId?: string
        personaName?: string | null
        customerName?: string | null
      } | null>(`/evaluations/customer/${customerId}?dealershipId=${encodeURIComponent(dealershipId)}`)
      return response
    } catch (error) {
      console.error('Failed to fetch evaluation results:', error)
      return null
    }
  },
}
